/**
 * GlobalErrorHandler
 *
 * Purpose
 * - Centralizes error handling for the Angular client.
 * - Hides the spinner, logs client-side errors to the API via ReportService, and displays a critical error dialog.
 * - Avoids DI timing issues during bootstrap by resolving dependencies lazily via Injector.
 *
 * Usage
 * - Registered in AppModule providers:
 *   { provide: ErrorHandler, useClass: GlobalErrorHandler, deps: [AppConfig, Injector] }
 * - Do not inject ReportService/MatDialog directly in the constructor; use Injector in handleError to avoid DI cycles.
 * - Ensure AppConfig is initialized at startup via APP_INITIALIZER so ReportService has a baseUrl.
 *
 * Changes since previous implementation
 * - Lazy resolution with Injector:
 *   - ReportService, SpinnerService, MessageService, and MatDialog are resolved inside handleError() instead of via constructor.
 * - Config readiness guard:
 *   - Defers work when AppConfig.settings.baseUrl isn’t initialized (bootstrap).
 * - Logout flow moved out of the dialog:
 *   - CriticalErrorDialogComponent emits a result; GlobalErrorHandler resolves AuthenticationService lazily and logs out on 'logout'.
 * - New: loop and safety guards:
 *   - Skips logging when there is no token (likely already logged out), when the failing request is the logger endpoint itself,
 *     when a hard logout is in progress (window.__rpmsLoggingOut), or while re-entrancy is detected for the same cascade.
 */

import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { SpinnerService } from './spinner.service';
import { CriticalErrorDialogComponent } from './critical-error-dialog/critical-error-dialog.component';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';
import { ReportService } from './report.service';
import { MessageService } from './message.service';
import { AppConfig } from '../app.config';
import { AuthenticationService } from '../core/authentication/shared/authentication.service';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

  criticalErrorDialogRef: MatDialogRef<CriticalErrorDialogComponent>;

  // Prevent re-entrant logging loops within a single error cascade
  private isHandling = false;

  constructor(
    private appConfig: AppConfig,
    private injector: Injector
  ) { }

  handleError(error: any): void {
    // If already handling an error, don't recurse further
    if (this.isHandling) {
      // Still surface to console
      console.error(error);
      return;
    }
    this.isHandling = true;

    try {
      // If config is not yet loaded (early bootstrap errors), skip to avoid DI/timing issues
    if (!AppConfig.settings || !AppConfig.settings.baseUrl) {
      console.warn('GlobalErrorHandler: config not loaded yet; deferring report/log.');
      // Still surface the error to the console for diagnostics.
      console.error(error);
      return;
    }

      // Skip logging if a hard logout is in progress (set by AuthInterceptor)
      if ((window as any).__rpmsLoggingOut === true) {
        console.warn('GlobalErrorHandler: logout in progress; skipping logClientMessage.');
        console.error(error);
        return;
      }

      // Skip logging when there is no token (already logged out or expired) to avoid 401 loops
      const hasToken = !!sessionStorage.getItem('rpmsToken');

      // Skip logging the logger’s own failures to avoid infinite loops
      const isLoggerFailure = this.isLoggerEndpointError(error);

    const _spinnerService = this.injector.get(SpinnerService);
    const _messageService = this.injector.get(MessageService);

    // Always hide spinner on error to unblock the UI.
    _spinnerService.handleProgressSpinnerVisibility('hide', 'Processing Request...');

      if (hasToken && !isLoggerFailure) {
        const _reportService = this.injector.get(ReportService);
    // Compose a simple payload; enrich as needed (e.g., correlation id, route, user).
    const msg = String(error?.message + '. ' + error?.stack);

    // Log the client-side error to the server.
    _reportService.logClientMessage({ message: msg })
      .subscribe(
        () => _messageService.showToastBottomLeft('Logged error'),
        (response) => _messageService.onFailure('Failed to log error.', response)
      );

      }

      // Show a critical error dialog with user guidance (only if config loaded and not logging out)
    this.showLockoutAlert(error);

    // Preserve original console logging for dev diagnostics.
    console.error(error);
    } finally {
      // Allow subsequent, unrelated errors to be handled
      this.isHandling = false;
    }
  }

  private isLoggerEndpointError(error: any): boolean {
    // Covers HttpErrorResponse or nested errors with a url/message containing the logging endpoint
    try {
      const url = (error as HttpErrorResponse)?.url || (error?.error && error.error.url) || '';
      const msg = (error?.message || '') as string;
      return (url && url.indexOf('/api/LogActions/LogClientMessage') !== -1) ||
             (msg && msg.indexOf('/api/LogActions/LogClientMessage') !== -1);
    } catch {
      return false;
    }
  }

  private showLockoutAlert(error: any): void {
    // Resolve MatDialog lazily to avoid cycles.
    const _dialog = this.injector.get(MatDialog);
    this.criticalErrorDialogRef = _dialog.open(CriticalErrorDialogComponent, {
      disableClose: true,
      data: {
        message:
          error.message +
          '<br/><br/>Please contact support if this error persists.' +
          '<br/><br/><strong>It is recommended that you log out and then log back in before continuing your work. ' +
          'Continuing to work without logging out could have unexpected results.' +
          '<br/><br/>Do you want to continue working, or log out?</strong>'
      }
    });

    this.criticalErrorDialogRef.afterClosed().subscribe(result => {
      // result should be 'logout' | 'continue'
      if (result === 'logout') {
        const auth = this.injector.get(AuthenticationService);
        auth.logout();
      }
      this.criticalErrorDialogRef = null;
    });
  }
}
